import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaHospital, FaSave, FaSms } from 'react-icons/fa';
import './Healthcare.css';
import './Doctors.css';

const HospitalSettings = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/healthcare/hospital', { headers });
      setForm(res.data);
    } catch (err) {
      console.error('Failed to load hospital settings:', err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      await axios.put('/api/healthcare/hospital', {
        name: form.name,
        sms_confirmation_enabled: !!form.sms_confirmation_enabled,
        sms_confirmation_message: form.sms_confirmation_message,
        sms_reminder_enabled: !!form.sms_reminder_enabled,
        sms_reminder_message: form.sms_reminder_message,
        sms_cancellation_enabled: !!form.sms_cancellation_enabled,
        sms_cancellation_message: form.sms_cancellation_message,
      }, { headers });
      setMessage(t('hospitalSettings.saved', 'Settings saved!'));
    } catch (err) {
      setMessage(err?.response?.data?.detail || t('hospitalSettings.saveError', 'Failed to save settings.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return <div className="hc-page"><div className="hc-state">{t('hospitalSettings.loading', 'Loading...')}</div></div>;
  }

  return (
    <div className="hc-page">
      <header className="hc-header">
        <div className="hc-header-title">
          <FaHospital className="hc-header-icon" />
          <div>
            <h1>{t('hospitalSettings.title', 'Hospital Settings')}</h1>
            <p>{t('hospitalSettings.subtitle', 'Hospital details and patient SMS notifications.')}</p>
          </div>
        </div>
        <button type="button" className="hc-btn primary" onClick={save} disabled={saving}>
          <FaSave /> {saving ? t('hospitalSettings.saving', 'Saving...') : t('hospitalSettings.save', 'Save')}
        </button>
      </header>

      {message && <div className="dr-alert" style={{ background: 'rgba(18,184,184,.1)', borderColor: 'rgba(18,184,184,.3)', color: '#12b8b8' }}>{message}</div>}

      <section className="hc-slots-card" style={{ marginBottom: 18 }}>
        <div className="dr-field">
          <label>{t('hospitalSettings.name', 'Hospital name')}</label>
          <input value={form.name || ''} onChange={(e) => setField('name', e.target.value)} />
        </div>
      </section>

      <section className="hc-slots-card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}><FaSms /> {t('hospitalSettings.sms', 'Patient SMS')}</h3>
        <p className="hc-muted" style={{ marginBottom: 14 }}>{t('hospitalSettings.smsHint', 'Uses your Twilio account if configured, otherwise the platform SMS. Placeholders: {name}, {doctor}, {date}, {time}.')}</p>

        <div className="dr-check" style={{ marginBottom: 10 }}>
          <label><input type="checkbox" checked={!!form.sms_confirmation_enabled} onChange={(e) => setField('sms_confirmation_enabled', e.target.checked)} /> {t('hospitalSettings.confirmEnabled', 'Send a confirmation SMS when an appointment is booked')}</label>
        </div>
        {form.sms_confirmation_enabled && (
          <div className="dr-field" style={{ marginBottom: 18 }}>
            <label>{t('hospitalSettings.confirmMessage', 'Confirmation message')}</label>
            <textarea rows="2" value={form.sms_confirmation_message || ''} onChange={(e) => setField('sms_confirmation_message', e.target.value)} />
          </div>
        )}

        <div className="dr-check" style={{ marginBottom: 10 }}>
          <label><input type="checkbox" checked={!!form.sms_reminder_enabled} onChange={(e) => setField('sms_reminder_enabled', e.target.checked)} /> {t('hospitalSettings.reminderEnabled', 'Send a reminder SMS 2 days before the appointment')}</label>
        </div>
        {form.sms_reminder_enabled && (
          <div className="dr-field" style={{ marginBottom: 18 }}>
            <label>{t('hospitalSettings.reminderMessage', 'Reminder message')}</label>
            <textarea rows="2" value={form.sms_reminder_message || ''} onChange={(e) => setField('sms_reminder_message', e.target.value)} />
          </div>
        )}

        <div className="dr-check" style={{ marginBottom: 10 }}>
          <label><input type="checkbox" checked={!!form.sms_cancellation_enabled} onChange={(e) => setField('sms_cancellation_enabled', e.target.checked)} /> {t('hospitalSettings.cancelEnabled', 'Notify the patient by SMS when an appointment is cancelled')}</label>
        </div>
        {form.sms_cancellation_enabled && (
          <div className="dr-field">
            <label>{t('hospitalSettings.cancelMessage', 'Cancellation message')}</label>
            <textarea rows="2" value={form.sms_cancellation_message || ''} onChange={(e) => setField('sms_cancellation_message', e.target.value)} />
          </div>
        )}
      </section>
    </div>
  );
};

export default HospitalSettings;
